import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { store } from '../store';
import { addCompetition } from '../store/competitionSlice';
import {
  Typography,
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper
} from '@mui/material';
import BasicInformationForm from '../components/CompetitionForms/BasicInformationForm';
import SessionStructureForm from '../components/CompetitionForms/SessionStructureForm';
import CompetitionSettingsForm from '../components/CompetitionForms/CompetitionSettingsForm';

const CompetitionCreate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(() => {
    const saved = localStorage.getItem('competitionCreateStep');
    return saved ? parseInt(saved) : 0;
  });

  const [competition, setCompetition] = useState(() => {
    const saved = localStorage.getItem('competitionCreateForm');
    return saved ? JSON.parse(saved) : {
      name: '',
      startDate: null,
      endDate: null,
      venue: '',
      address: '',
      placeId: '',
      location: {
        lat: null,
        lng: null
      },
      colors: {
        primary: '#2196f3',
        secondary: '#f50057'
      },
      sessions: [],
      status: 'draft',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  });

  useEffect(() => {
    localStorage.setItem('competitionCreateForm', JSON.stringify(competition));
  }, [competition]);

  useEffect(() => {
    localStorage.setItem('competitionCreateStep', activeStep.toString());
  }, [activeStep]);

  const steps = [
    'Basic Information',
    'Session Structure',
    'Competition Settings'
  ];

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    try {
      // Create properly structured competition data
      const competitionData = {
        competition: {
          ...competition,
          startDate: competition.startDate ? new Date(competition.startDate).toISOString() : null,
          endDate: competition.endDate ? new Date(competition.endDate).toISOString() : null,
          id: Date.now().toString()
        },
        statistics: {
          totalTeams: 0,
          totalGymnasts: 0,
          totalScratchedGymnasts: 0,
          totalJudges: 0,
          totalSessions: competition.sessions?.length || 0,
          gymnastsByLevel: {},
          gymnastsByAgeGroup: {}
        },
        sessions: competition.sessions || [],
        teams: [],
        gymnasts: [],
        levels: [],
        judges: [],
        coaches: [],
        ageGroups: [],
        events: [],
        rotations: []
      };
      
      await dispatch(addCompetition(competitionData));
      const competitions = store.getState().competition.competitions;
      const newCompetitionId = competitions[competitions.length - 1].competition.id;
      localStorage.removeItem('competitionCreateForm');
      localStorage.removeItem('competitionCreateStep');
      navigate(`/competition/${newCompetitionId}`);
    } catch (error) {
      console.error('Failed to create competition:', error);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <BasicInformationForm
            competition={competition}
            onUpdate={setCompetition}
          />
        );
      case 1:
        return (
          <SessionStructureForm
            competition={competition}
            onUpdate={setCompetition}
          />
        );
      case 2:
        return (
          <CompetitionSettingsForm
            competition={competition}
            onUpdate={setCompetition}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth={false}>
      <Paper sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Create Competition
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ my: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 4, mb: 4 }}>
          {renderStepContent(activeStep)}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            onClick={() => {
              localStorage.removeItem('competitionCreateForm');
              localStorage.removeItem('competitionCreateStep');
              navigate('/');
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Box>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              Back
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
              >
                Create Competition
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default CompetitionCreate;
